/**
 * Work Log Review top-N bar — task 159c §D7, locked decision #7.
 *
 * Renders the first 10 group rows from the (server-sorted) stats table
 * as a horizontal-ish ECharts bar. Reuses the `Chart` primitive locked
 * in by task 157.
 *
 * When groupBy is empty, the chart shows a single "All filtered events"
 * bar for the filter total. When groupBy is stacked, the bar label is
 * the last dimension's value with the prior dimensions joined as
 * tooltip context.
 */

import { Chart, useChartTheme } from '@/components/primitives/Chart';
import type { GroupByDimension, SortByColumn, WorkLogReviewGroupRow } from '@/types/workLogReview';
import styles from './WorkLogReviewTopNChart.module.css';

const MAX_BARS = 10;
const CHART_HEIGHT = 280;

function renderGroupLabel(
  row: WorkLogReviewGroupRow,
  groupBy: readonly GroupByDimension[],
): string {
  if (groupBy.length === 0) return 'All filtered events';
  return groupBy.map((dim) => row.key[dim] ?? '(none)').join(' › ');
}

function metricFor(row: WorkLogReviewGroupRow, sortBy: SortByColumn): number {
  switch (sortBy) {
    case 'count':
      return row.count;
    case 'hours':
      return +row.hours.toFixed(2);
    case 'distinctEmployees':
      return row.distinctEmployees;
    case 'percentOfFiltered':
      return +row.percentOfFiltered.toFixed(2);
    case 'firstSeenAt':
    case 'lastSeenAt':
      // Time-axis sorts are not meaningful as a chart metric — fall back
      // to event count so the user always sees a sensible bar.
      return row.count;
  }
}

function metricLabel(sortBy: SortByColumn): string {
  switch (sortBy) {
    case 'count':
      return 'Events';
    case 'hours':
      return 'Hours';
    case 'distinctEmployees':
      return 'People';
    case 'percentOfFiltered':
      return '% of filtered';
    case 'firstSeenAt':
    case 'lastSeenAt':
      return 'Events';
  }
}

interface WorkLogReviewTopNChartProps {
  rows: readonly WorkLogReviewGroupRow[];
  groupBy: readonly GroupByDimension[];
  sortBy: SortByColumn;
}

export function WorkLogReviewTopNChart({ rows, groupBy, sortBy }: WorkLogReviewTopNChartProps) {
  const theme = useChartTheme();
  const top = rows.slice(0, MAX_BARS);
  const hasData = top.length > 0;

  const labels = top.map((r) => renderGroupLabel(r, groupBy));
  const values = top.map((r) => metricFor(r, sortBy));
  const yLabel = metricLabel(sortBy);

  // ECharts diffs the option object by reference; React Compiler memoizes
  // the component as a whole, so a fresh literal here doesn't cost extra
  // work on stable inputs.
  const option = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: 56, right: 24, top: 24, bottom: 80 },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: {
        interval: 0,
        rotate: labels.some((l) => l.length > 12) ? 30 : 0,
        color: theme.textMuted,
      },
      axisLine: { lineStyle: { color: theme.border } },
    },
    yAxis: {
      type: 'value',
      name: yLabel,
      nameTextStyle: { color: theme.textMuted },
      axisLabel: { color: theme.textMuted },
      splitLine: { lineStyle: { color: theme.border } },
    },
    series: [
      {
        name: yLabel,
        type: 'bar',
        data: values,
        itemStyle: { color: theme.colors[0] },
      },
    ],
  };

  if (!hasData) {
    return <p className={styles.empty}>No groups to chart in the current filter window.</p>;
  }

  const fallback =
    `Top ${top.length} groups by ${yLabel}: ` +
    top.map((_row, i) => `${labels[i]} ${values[i]}`).join('; ');

  return (
    <div className={styles.wrapper} data-testid="wlr-topn-chart">
      <Chart
        option={option}
        height={CHART_HEIGHT}
        ariaLabel={`Top ${top.length} Work Log Review groups by ${yLabel}`}
        textFallback={fallback}
      />
    </div>
  );
}
